import React from 'react';
import { styled, useTheme } from 'styled-components';

import { Loader } from '../../V2/Elements/Loader/index.tsx';

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  rows?: number;
  loading?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  clearButton?: boolean;
  dataTestId?: string;
}

const StyledInput = styled.input<{ rows?: number; $clearButton?: boolean }>`
  border-radius: 4px;
  width: 100%;
  border: solid 1px ${({ theme }) => theme.colors.grey30_1};
  height: ${({ rows }) => (rows && rows > 1 ? `${rows * 25}px` : '38px')};
  padding: ${({ $clearButton }) => ($clearButton ? '8px 38px 8px 12px' : '8px 12px')};
  font-size: 14px;
  font-family: CircularXXWeb, Roboto, sans-serif;
  color: ${({ theme }) => theme.colors.black_1};
  resize: none;
  transition: all 100ms;
  appearance: none;
  background-color: ${({ theme }) => theme.colors.grey00_7};

  &::placeholder {
    color: ${({ theme }) => theme.colors.grey70_1};
  }

  &[disabled],
  &[readonly] {
    background-color: ${({ theme }) => theme.colors.grey05_3};
    border-color: ${({ theme }) => theme.colors.grey30_2};
  }

  &[disabled] {
    color: ${({ theme }) => theme.colors.grey100_2};
    &::placeholder {
      color: ${({ theme }) => theme.colors.grey100_2};
    }
  }

  &:focus {
    border-color: ${({ theme }) => theme.colors.main};
    outline: none;
    box-shadow: 0 0 0 1px ${({ theme }) => theme.colors.main};
  }

  &:not([disabled], [readonly], :focus):hover {
    border-color: ${({ theme }) => theme.colors.grey70_1};
  }
`;

const InputContainer = styled.div`
  position: relative;
  width: 100%;
`;

const IconContainer = styled.div`
  position: absolute;
  right: 12px;
  top: calc(50% - 7px);
`;

const Input = React.forwardRef<HTMLInputElement, InputProps>(function Input({ loading, dataTestId, ...props }, ref) {
  const { colors } = useTheme();

  return (
    <InputContainer data-testid={dataTestId}>
      <StyledInput {...props} ref={ref} />
      {loading ? (
        <IconContainer>
          <Loader fill={colors.grey100_1} background='transparent' />
        </IconContainer>
      ) : null}
    </InputContainer>
  );
});

export { Input };
